if (!customElements.get('ingredients-cards-list')) {
  customElements.define(
    'ingredients-cards-list',
    class IngredientsCardsList extends HTMLElement {
      connectedCallback() {
        this._swiper = null;
        this._layout = this.dataset.layout || 'stacked';
        this._onToggleClick = this._onToggleClick.bind(this);
        this.addEventListener('click', this._onToggleClick);

        this._variantCardsMap = null;
        this._defaultCardsHtml = null;
        this._renderedCardsKey = 'default';
        this._didSyncInitialVariant = false;
        this._onVariantChange = this._onVariantChange.bind(this);
        const variantMapEl = this.querySelector('[data-variant-cards-map]');
        const cardsWrapper = this.querySelector('.section-ingredients__cards-swiper > .swiper-wrapper');
        if (variantMapEl && cardsWrapper) {
          try {
            this._variantCardsMap = JSON.parse(variantMapEl.textContent);
            this._defaultCardsHtml = cardsWrapper.innerHTML;
            document.addEventListener('mp4:variant-change', this._onVariantChange);
            requestAnimationFrame(() => this._syncInitialVariant());
          } catch (error) {
            console.warn('ingredients-cards-list: invalid variant cards map:', error);
          }
        }

        if (this._layout === 'slider') {
          // Slider layout: swiper at every width, dots below, slides
          // bleeding off the right edge (video reviews pattern).
          this._equalize = this._equalizeCardHeights.bind(this);
          this._onResize = this._onResize.bind(this);
          this._resizeRaf = 0;
          window.addEventListener('resize', this._onResize);
          // Card images are lazy — re-equalize once everything has loaded.
          window.addEventListener('load', this._equalize);
          this._initSliderSwiper();
          return;
        }

        // Stacked layout: swiper on mobile only, 3-column grid on desktop.
        this._mq = window.matchMedia('(max-width: 767px)');
        this._onChange = this._onChange.bind(this);
        this._mq.addEventListener('change', this._onChange);

        if (this._mq.matches) {
          this._initSwiper();
        } else {
          this._resetLayout();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onToggleClick);
        if (this._mq) this._mq.removeEventListener('change', this._onChange);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._equalize) window.removeEventListener('load', this._equalize);
        document.removeEventListener('mp4:variant-change', this._onVariantChange);
        cancelAnimationFrame(this._resizeRaf);
        this._destroySwiper();
      }

      _onVariantChange(event) {
        if (!this._variantCardsMap) return;

        const detail = event.detail || {};
        if (String(detail.productId) !== String(this.dataset.productId)) return;

        this._showCardsForVariant(detail.variantId, detail.variantContentAllowed === true);
      }

      _showCardsForVariant(variantId, variantContentAllowed) {
        const variantKey = String(variantId || '');
        const hasVariantCards =
          variantContentAllowed &&
          Object.prototype.hasOwnProperty.call(this._variantCardsMap, variantKey);
        const nextKey = hasVariantCards ? variantKey : 'default';
        if (this._renderedCardsKey === nextKey) return;

        const wrapper = this.querySelector('.section-ingredients__cards-swiper > .swiper-wrapper');
        if (!wrapper) return;

        wrapper.innerHTML = hasVariantCards
          ? this._variantCardsMap[variantKey]
          : this._defaultCardsHtml;
        this._renderedCardsKey = nextKey;

        if (this._swiper) {
          this._swiper.update();
          this._swiper.slideTo(0, 0);
        } else {
          this._resetLayout();
        }

        if (this._layout === 'slider') {
          requestAnimationFrame(() => {
            this._equalizeCardHeights();
            this.querySelectorAll('.section-ingredients__card img').forEach((image) => {
              if (!image.complete) image.addEventListener('load', this._equalize, { once: true });
            });
          });
        }
      }

      _onResize() {
        cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(this._equalize);
      }

      /* All CLOSED cards share the height of the tallest one (min-height);
         an opened card simply grows past it — neighbours stay untouched.
         Open dropdowns are forced closed (via the measuring class, no
         transition) during measurement so they don't skew the max. */
      _equalizeCardHeights() {
        const cards = Array.from(this.querySelectorAll('.section-ingredients__card'));
        if (cards.length === 0) return;

        this.classList.add('section-ingredients__cards-list--measuring');
        cards.forEach((card) => (card.style.minHeight = ''));
        const max = Math.max(...cards.map((card) => card.offsetHeight));
        cards.forEach((card) => (card.style.minHeight = max + 'px'));
        this.classList.remove('section-ingredients__cards-list--measuring');
      }

      /* Card details dropdown (slider layout): toggles ONLY the clicked
         card — other cards keep their own open/closed state and height. */
      _onToggleClick(event) {
        const toggle = event.target.closest('.section-ingredients__card-toggle');
        if (!toggle || !this.contains(toggle)) return;
        const card = toggle.closest('.section-ingredients__card');
        if (!card) return;
        const open = card.classList.toggle('open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.setAttribute('aria-label', open ? 'Hide details' : 'Show details');
      }

      _onChange(event) {
        if (event.matches) {
          this._initSwiper();
        } else {
          this._destroySwiper();
          this._resetLayout();
        }
      }

      /* ---------- slider layout ---------- */

      /* The slider spans the full viewport (CSS pulls it out by --si-bleed
         via negative margins) — slides are offset by that same distance so
         they still start/end aligned with the content edge, while
         swiped-away cards clip at the viewport edge. Reading the applied
         margin keeps JS and CSS in sync automatically. */
      _sidePadding() {
        return Math.abs(parseFloat(getComputedStyle(this).marginLeft)) || 0;
      }

      _initSliderSwiper() {
        const init = () => {
          if (typeof Swiper === 'undefined') return;

          const el = this.querySelector('.section-ingredients__cards-swiper');
          const pagination = this.querySelector('.section-ingredients__cards-pagination');
          if (!el || this._swiper) return;

          const sidePadding = this._sidePadding();

          this._swiper = new Swiper(el, {
            slideClass: 'shopify-block',
            // Slide widths come from CSS (card max width, capped on small
            // screens) so the visual gap is exactly spaceBetween.
            slidesPerView: 'auto',
            spaceBetween: 24,
            slidesOffsetBefore: sidePadding,
            slidesOffsetAfter: sidePadding,
            watchOverflow: true,
            pagination: pagination
              ? {
                  el: pagination,
                  clickable: true,
                }
              : false,
            on: {
              resize: (swiper) => {
                const pad = this._sidePadding();
                if (swiper.params.slidesOffsetBefore !== pad) {
                  swiper.params.slidesOffsetBefore = pad;
                  swiper.params.slidesOffsetAfter = pad;
                  swiper.update();
                }
              },
            },
          });

          this._equalizeCardHeights();

          // Main Product v4 can finish its initial selection before this
          // component is registered. Read its current state so that first
          // render still receives variant-specific cards.
          this._syncInitialVariant();
        };

        if (typeof Swiper !== 'undefined') {
          init();
        } else {
          setTimeout(init, 300);
        }
      }

      /* ---------- stacked layout (mobile swiper) ---------- */

      _calcSlidesOffsetAfter(width) {
        const slidesPerView = 1.12;
        const spaceBetween = 12;
        const slideWidth = (width - spaceBetween * (slidesPerView - 1)) / slidesPerView;
        const nextPeek = width - slideWidth - spaceBetween;
        const rightPadding = 15;

        return Math.round(nextPeek + rightPadding);
      }

      _initSwiper() {
        if (this._swiper || !this._mq.matches) return;

        const init = () => {
          if (typeof Swiper === 'undefined') return;

          const el = this.querySelector('.section-ingredients__cards-swiper');
          const scrollbar = this.querySelector('.section-ingredients__cards-scrollbar');
          if (!el || this._swiper) return;

          const slidesOffsetAfter = this._calcSlidesOffsetAfter(el.clientWidth);

          this._swiper = new Swiper(el, {
            slideClass: 'shopify-block',
            slidesPerView: 1.12,
            spaceBetween: 12,
            slidesOffsetAfter,
            watchOverflow: true,
            scrollbar: scrollbar
              ? {
                  el: scrollbar,
                  draggable: true,
                }
              : false,
            on: {
              resize: (swiper) => {
                const offsetAfter = this._calcSlidesOffsetAfter(swiper.width);

                if (swiper.params.slidesOffsetAfter !== offsetAfter) {
                  swiper.params.slidesOffsetAfter = offsetAfter;
                  swiper.update();
                }
              },
            },
          });

          this._syncInitialVariant();
        };

        if (typeof Swiper !== 'undefined') {
          init();
        } else {
          setTimeout(init, 300);
        }
      }

      _destroySwiper() {
        if (!this._swiper) return;

        this._swiper.destroy(true, true);
        this._swiper = null;
        this._resetLayout();
      }

      _resetLayout() {
        const wrapper = this.querySelector('.section-ingredients__cards-swiper > .swiper-wrapper');
        const slides = this.querySelectorAll(
          '.section-ingredients__cards-swiper > .swiper-wrapper > .shopify-block'
        );

        wrapper?.removeAttribute('style');
        slides.forEach((slide) => slide.removeAttribute('style'));
      }

      _syncInitialVariant() {
        if (!this._variantCardsMap || this._didSyncInitialVariant) return;

        const mainProduct = Array.from(document.querySelectorAll('main-product-v4')).find(
          (element) => String(element.dataset.productId) === String(this.dataset.productId)
        );
        if (!mainProduct || !mainProduct.dataset.variant) return;

        this._didSyncInitialVariant = true;
        this._showCardsForVariant(
          mainProduct.dataset.variant,
          mainProduct.dataset.variantContentAllowed === 'true'
        );
      }
    }
  );
}
